import { type ReactNode, useCallback, useRef } from 'react';
import { BarChart3, Code2, Copy, Filter, GitCompareArrows, LayoutDashboard, Link2, Pencil, Rows3, FileJson } from 'lucide-react';
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from '../../../../components/ui/context-menu';
import { writeClipboardText } from '../../../../lib/clipboard';
import { NumericGroupingMenu } from './NumericGroupingMenu';

type CellAction = (columnName: string, value: unknown) => void;

type CellContextMenuBaseProps = {
  children: ReactNode;
  value: unknown;
  columnName?: string;
  canEdit: boolean;
  isEditMode: boolean;
  onEdit: () => void;
  onEnterEditMode?: () => void;
  onCopySelection?: () => void;
  onCopyCell?: () => void;
  onCopyRow?: () => void;
  isCopying?: boolean;
  /** S20 — "Pin to dashboard" (QE results surface; parent supplies provenance). */
  onPinToDashboard?: () => void;
  onProfileColumn?: (columnName: string) => void;
  /** When true (column is `json-eligible` AND loaded values look like JSON),
   *  render a "Declare as JSON" entry that calls `onDeclareAsJson`. */
  canDeclareAsJson?: boolean;
  onDeclareAsJson?: (columnName: string) => void;
  /** When true (column is `declared-json`), render a "JSON details" entry
   * that calls `onShowJsonDetails`. Replaces the "Declare as JSON" entry —
   * a column can't be both declarable and already-declared. */
  canShowJsonDetails?: boolean;
  onShowJsonDetails?: (columnName: string) => void;
  /** When true (column has at least one loose-FK declaration), render an
   * "Edit loose relationship" entry that calls `onEditLooseFk`. */
  canEditLooseFk?: boolean;
  onEditLooseFk?: (columnName: string) => void;
  /** When true (column has no loose FK yet and is eligible to declare one),
   * render a "Declare loose relationship" entry that calls
   * `onDeclareLooseFk`. Mutually exclusive with the Edit entry — a column
   * shouldn't be both declarable and already-declared. */
  canDeclareLooseFk?: boolean;
  onDeclareLooseFk?: (columnName: string) => void;
  /** T30 — when true (the selection spans exactly two cells that BOTH resolve through
   * `resolveSpatialCellInput`), render a "Compare geometries" entry that calls `onCompareGeometry`. */
  canCompareGeometry?: boolean;
  onCompareGeometry?: () => void;
  /** PP-01 — when true (the cell's column is numeric), render the shared
   * "Number display" submenu that flips the global thousands-separator mode. */
  canToggleNumericGrouping?: boolean;
  /** Open/close notifications from the underlying Radix root. Used by the
   * grids' delegated single-menu pattern to clear the tracked target cell
   * (and its open-state highlight) when the menu closes. */
  onOpenChange?: (open: boolean) => void;
};

// "Add to WHERE" (Query Editor) and "Add to filter" (Table Explorer) are
// mutually exclusive — each surface gets exactly one.
type CellContextMenuFilterProps =
  | { onAddToWhere?: CellAction; onAddColumnToFilter?: never }
  | { onAddToWhere?: never; onAddColumnToFilter?: CellAction };

type CellContextMenuProps = CellContextMenuBaseProps & CellContextMenuFilterProps;

function formatCopyValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function CellContextMenu({
  children,
  value,
  columnName,
  canEdit,
  isEditMode,
  onEdit,
  onEnterEditMode,
  onCopySelection,
  onCopyCell,
  onCopyRow,
  isCopying,
  onAddToWhere,
  onAddColumnToFilter,
  onPinToDashboard,
  onProfileColumn,
  canDeclareAsJson,
  onDeclareAsJson,
  canShowJsonDetails,
  onShowJsonDetails,
  canEditLooseFk,
  onEditLooseFk,
  canDeclareLooseFk,
  onDeclareLooseFk,
  canCompareGeometry,
  onCompareGeometry,
  canToggleNumericGrouping,
  onOpenChange,
}: CellContextMenuProps) {
  const shouldPreventCloseAutoFocusRef = useRef(false);

  const handleCopy = useCallback(() => {
    if (onCopySelection) {
      onCopySelection();
    } else if (onCopyCell) {
      onCopyCell();
    } else {
      void writeClipboardText(formatCopyValue(value));
    }
  }, [value, onCopySelection, onCopyCell]);

  const handleEdit = useCallback(() => {
    // When switching to an inline editor on close, don't let Radix restore focus to the trigger.
    shouldPreventCloseAutoFocusRef.current = true;
    if (!isEditMode && onEnterEditMode) {
      onEnterEditMode();
    }
    onEdit();
  }, [isEditMode, onEnterEditMode, onEdit]);

  const handleAddToWhere = useCallback(() => {
    if (onAddToWhere && columnName) {
      onAddToWhere(columnName, value);
    }
  }, [onAddToWhere, columnName, value]);

  const handleAddColumnToFilter = useCallback(() => {
    if (onAddColumnToFilter && columnName) {
      onAddColumnToFilter(columnName, value);
    }
  }, [onAddColumnToFilter, columnName, value]);

  return (
    <ContextMenu onOpenChange={onOpenChange}>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent
        onCloseAutoFocus={(event) => {
          if (shouldPreventCloseAutoFocusRef.current) {
            event.preventDefault();
            shouldPreventCloseAutoFocusRef.current = false;
          }
        }}
      >
        <ContextMenuItem onClick={handleCopy} disabled={isCopying} data-testid="context-menu-copy-value">
          <Copy className="h-3.5 w-3.5 mr-2" />
          {onCopySelection ? 'Copy selection' : 'Copy cell'}
        </ContextMenuItem>
        {onCopyRow && (
          <ContextMenuItem onClick={onCopyRow} disabled={isCopying} data-testid="context-menu-copy-row">
            <Rows3 className="h-3.5 w-3.5 mr-2" />
            Copy row
          </ContextMenuItem>
        )}
        <ContextMenuSeparator />
        <ContextMenuItem onClick={handleEdit} disabled={!canEdit} data-testid="context-menu-edit-value">
          <Pencil className="h-3.5 w-3.5 mr-2" />
          Edit value
        </ContextMenuItem>
        {onAddToWhere && columnName && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem onClick={handleAddToWhere} data-testid="context-menu-add-to-where">
              <Filter className="h-3.5 w-3.5 mr-2" />
              Add to WHERE
            </ContextMenuItem>
          </>
        )}
        {onAddColumnToFilter && columnName && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              onClick={handleAddColumnToFilter}
              data-testid="context-menu-add-to-filter"
            >
              <Filter className="h-3.5 w-3.5 mr-2" />
              Add to filter
            </ContextMenuItem>
          </>
        )}
        {onProfileColumn && columnName && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              onClick={() => onProfileColumn(columnName)}
              data-testid="context-menu-profile-column"
            >
              <BarChart3 className="h-3.5 w-3.5 mr-2" />
              Profile this column
            </ContextMenuItem>
          </>
        )}
        {onPinToDashboard && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              onClick={onPinToDashboard}
              data-testid="context-menu-pin-to-dashboard"
            >
              <LayoutDashboard className="h-3.5 w-3.5 mr-2" />
              Pin to dashboard
            </ContextMenuItem>
          </>
        )}
        {canShowJsonDetails && onShowJsonDetails && columnName && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              onClick={() => onShowJsonDetails(columnName)}
              data-testid="context-menu-json-details"
            >
              <FileJson className="h-3.5 w-3.5 mr-2" />
              JSON details
            </ContextMenuItem>
          </>
        )}
        {canDeclareAsJson && onDeclareAsJson && columnName && !canShowJsonDetails && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              onClick={() => onDeclareAsJson(columnName)}
              data-testid="context-menu-declare-json"
            >
              <Code2 className="h-3.5 w-3.5 mr-2" />
              Declare as JSON
            </ContextMenuItem>
          </>
        )}
        {canEditLooseFk && onEditLooseFk && columnName && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              onClick={() => onEditLooseFk(columnName)}
              data-testid="context-menu-edit-loose-fk"
            >
              <Pencil className="h-3.5 w-3.5 mr-2" />
              Edit loose relationship
            </ContextMenuItem>
          </>
        )}
        {canDeclareLooseFk && onDeclareLooseFk && columnName && !canEditLooseFk && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              onClick={() => onDeclareLooseFk(columnName)}
              data-testid="context-menu-declare-loose-fk"
            >
              <Link2 className="h-3.5 w-3.5 mr-2" />
              Declare loose relationship
            </ContextMenuItem>
          </>
        )}
        {canCompareGeometry && onCompareGeometry && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem onClick={onCompareGeometry} data-testid="context-menu-compare-geometry">
              <GitCompareArrows className="h-3.5 w-3.5 mr-2" />
              Compare geometries
            </ContextMenuItem>
          </>
        )}
        {canToggleNumericGrouping && (
          <>
            <ContextMenuSeparator />
            <NumericGroupingMenu />
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
